// PipelinesTab.js
// Displayed within the Admin Panel as the "Pipelines" tab.
// Shows a log of automated pipeline runs read from uiaa-pipeline-logs via
// GET /admin/pipelines. Scoped to admin + superadmin only (enforced by API Gateway
// and pipeline_api.py Lambda — the tab itself is only rendered for those groups).
//
// Design: log-style table, newest run first.
// Extensible: when new pipelines write to uiaa-pipeline-logs, they appear
// automatically under a new pipeline name — no frontend changes needed.

import { useState, useEffect } from 'react';
import { apiRequest } from '../utils/api';
import { Spinner, EmptyState, Badge } from './ui';

// Friendly display names for pipeline identifiers written by Lambdas.
// Add a new entry here when a new pipeline comes online.
const PIPELINE_LABELS = {
  fulfillment: 'TED Membership Import',
  // qualtrics: 'Qualtrics Survey Export',   // future
};

// Format an ISO timestamp (e.g. "2026-07-01T04:03:22.456Z") into a readable string.
function formatTimestamp(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  if (isNaN(d)) return iso;
  return d.toLocaleString('en-US', {
    month:   'short',
    day:     'numeric',
    year:    'numeric',
    hour:    'numeric',
    minute:  '2-digit',
    hour12:  true,
  });
}

// Derive a status badge for a manifest row.
// A run with errors is a warning; a run with 0 rows total is suspicious.
function runStatus(manifest) {
  if (manifest.error)        return { label: 'Read Error', variant: 'error' };
  if (manifest.error_count > 0) return { label: 'Partial',    variant: 'warning' };
  if (manifest.row_count === 0) return { label: 'Empty',      variant: 'warning' };
  return                           { label: 'Success',     variant: 'success' };
}

export default function PipelinesTab() {
  const [manifests, setManifests] = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [error,     setError]     = useState(null);
  const [filter,    setFilter]    = useState('all'); // 'all' or a pipeline key

  useEffect(() => {
    setLoading(true);
    setError(null);

    const query = filter !== 'all' ? `?pipeline=${filter}` : '';
    apiRequest(`/admin/pipelines${query}`)
      .then(data => setManifests(data.manifests || []))
      .catch(err => setError(err.message || 'Failed to load pipeline logs'))
      .finally(() => setLoading(false));
  }, [filter]);

  // Derive pipeline filter options from returned manifests
  const pipelineNames = [...new Set(manifests.map(m => m.pipeline).filter(Boolean))].sort();

  const thClass = 'px-4 py-3 text-left text-[10px] font-semibold text-[var(--color-text-muted)] uppercase tracking-wider whitespace-nowrap';
  const tdClass = 'px-4 py-3 text-xs text-[var(--color-text-secondary)] align-top';

  return (
    <div className="space-y-4">

      {/* Header row */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-sm font-semibold text-[var(--color-text-primary)]">Pipeline Runs</h2>
          <p className="text-xs text-[var(--color-text-muted)] mt-0.5">
            Automated data imports into Project 217 — most recent first.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {/* Pipeline filter — only shown once we have more than one pipeline */}
          {pipelineNames.length > 1 && (
            <select
              value={filter}
              onChange={e => setFilter(e.target.value)}
              className="text-xs border border-[var(--color-border)] rounded-[var(--radius-lg)] px-3 h-8
                         focus:outline-none focus:ring-1 focus:ring-[var(--color-orange)]
                         text-[var(--color-text-primary)] bg-white cursor-pointer"
            >
              <option value="all">All Pipelines</option>
              {pipelineNames.map(p => (
                <option key={p} value={p}>{PIPELINE_LABELS[p] || p}</option>
              ))}
            </select>
          )}
          <button
            onClick={() => setFilter(f => f)} // re-trigger useEffect
            className="text-xs text-[var(--color-orange)] hover:underline"
          >
            Refresh
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="bg-white rounded-[var(--radius-xl)] border border-[var(--color-border)] overflow-hidden shadow-[var(--shadow-xs)]">
        {loading ? (
          <div className="flex items-center justify-center py-16 gap-2 text-xs text-[var(--color-text-muted)]">
            <Spinner size="sm" /> Loading pipeline logs...
          </div>
        ) : error ? (
          <div className="py-10 px-6 text-center">
            <p className="text-xs text-[var(--color-error)]">{error}</p>
          </div>
        ) : manifests.length === 0 ? (
          <EmptyState
            title="No pipeline runs yet"
            description="Pipeline logs will appear here after the first automated import."
          />
        ) : (
          <div className="overflow-auto">
            <table className="w-full text-left">
              <thead className="bg-[var(--color-surface-alt)] border-b border-[var(--color-border)] sticky top-0 z-10">
                <tr>
                  <th className={thClass}>Pipeline</th>
                  <th className={thClass}>File</th>
                  <th className={thClass}>Run Time</th>
                  <th className={thClass}>Rows in File</th>
                  <th className={thClass}>Inserted</th>
                  <th className={thClass}>Updated</th>
                  <th className={thClass}>Skipped</th>
                  <th className={thClass}>Errors</th>
                  <th className={thClass}>Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[var(--color-border)]">
                {manifests.map((m, i) => {
                  const status   = runStatus(m);
                  const filename = m.source_key
                    ? m.source_key.split('/').pop()
                    : '—';

                  return (
                    <tr
                      key={i}
                      className="hover:bg-[var(--color-surface)] transition-colors"
                    >
                      <td className={`${tdClass} font-medium text-[var(--color-text-primary)]`}>
                        {PIPELINE_LABELS[m.pipeline] || m.pipeline || '—'}
                      </td>
                      <td className={`${tdClass} font-mono max-w-[200px]`}>
                        <span className="block truncate" title={filename}>{filename}</span>
                      </td>
                      <td className={tdClass}>{formatTimestamp(m.timestamp)}</td>
                      <td className={`${tdClass} text-center`}>{m.row_count ?? '—'}</td>
                      <td className={`${tdClass} text-center text-[var(--color-success)] font-medium`}>
                        {m.inserted ?? '—'}
                      </td>
                      <td className={`${tdClass} text-center`}>{m.updated  ?? '—'}</td>
                      <td className={`${tdClass} text-center`}>{m.skipped  ?? '—'}</td>
                      <td className={`${tdClass} text-center ${m.error_count > 0 ? 'text-[var(--color-error)] font-medium' : ''}`}>
                        {m.error_count ?? '—'}
                      </td>
                      <td className={tdClass}>
                        <Badge variant={status.variant}>{status.label}</Badge>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Footer note */}
      {!loading && !error && manifests.length > 0 && (
        <p className="text-[10px] text-[var(--color-text-muted)]">
          Showing the {manifests.length} most recent run{manifests.length !== 1 ? 's' : ''}.
          Logs are stored in <span className="font-mono">uiaa-pipeline-logs</span> S3 bucket.
        </p>
      )}
    </div>
  );
}